<template>
  <div class="space-y-6">
    <notifications position="top center" />

    <!-- Header Section -->
    <div class="bg-white shadow-sm border rounded-lg">
      <div class="px-6 py-4 flex items-center justify-between">
        <div>
          <h1 class="text-2xl font-semibold text-gray-900">Salesforce Campaign Sync Log</h1>
          <p class="text-sm text-gray-600 mt-1">Complete overview of all campaign sync status.</p>
        </div>
        <div class="flex items-center gap-3">
          <button @click="loadPage" class="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 transition-colors flex items-center">
            <svg class="w-4 h-4 mr-2" :class="{ 'animate-spin': loading }" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15"></path>
            </svg>
            Retry
          </button>
          <button @click="checkUncachedCampaigns" :disabled="uncachedCampaigns.length === 0" class="px-4 py-2 bg-green-600 text-white rounded-md hover:bg-green-700 transition-colors flex items-center disabled:opacity-50">
            <svg class="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"></path>
            </svg>
            Check Pending ({{ uncachedCampaigns.length }})
          </button>
          <!-- Bulk Sync Button -->
          <button
            @click="startBulkSync"
            :disabled="bulkSyncRunning || syncInProgress"
            class="px-4 py-2 bg-purple-600 text-white rounded-md hover:bg-purple-700 transition-colors flex items-center disabled:opacity-50 disabled:cursor-not-allowed"
          >
            <svg v-if="bulkSyncRunning" class="w-4 h-4 mr-2 animate-spin" fill="none" viewBox="0 0 24 24">
              <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
              <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
            </svg>
            <svg v-else class="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15"></path>
            </svg>
            {{ bulkSyncRunning ? 'Syncing...' : 'Start Bulk Sync' }}
          </button>
        </div>
      </div>

      <!-- Filters Section -->
      <div class="px-6 pb-4 border-t">
        <div class="flex flex-wrap items-center gap-3 text-sm mt-4">
          <label class="inline-flex items-center">Days back:
            <input type="number" v-model.number="filters.days" min="1" class="ml-2 w-24 rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500" />
          </label>

          <label class="inline-flex items-center">Per page:
            <select v-model.number="perPage" class="ml-2 rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500">
              <option :value="25">25</option>
              <option :value="50">50</option>
              <option :value="100">100</option>
              <option :value="200">200</option>
            </select>
          </label>

          <label class="inline-flex items-center">Status filter:
            <select v-model="filters.statusFilter" class="ml-2 rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500">
              <option value="all">All</option>
              <option value="synced">Synced</option>
              <option value="not_synced">Not Synced</option>
              <option value="error">Error</option>
              <option value="pending">Pending</option>
            </select>
          </label>

          <label class="inline-flex items-center">Health filter:
            <select v-model="filters.healthFilter" class="ml-2 rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500">
              <option value="all">All</option>
              <option value="healthy">Healthy</option>
              <option value="problematic">Problematic</option>
            </select>
          </label>

          <label class="inline-flex items-center">Amount filter:
            <select v-model="filters.amountFilter" class="ml-2 rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500">
              <option value="all">All</option>
              <option value="mismatch">Amount Mismatch</option>
              <option value="synced">Amount Synced</option>
              <option value="not_synced">Never Synced</option>
            </select>
          </label>

          <button @click="applyFilters" class="px-3 py-2 bg-white border border-gray-300 rounded-md hover:bg-gray-50">Apply Filters</button>

          <button @click="toggleIgnoredSection" class="px-3 py-2 bg-amber-50 border border-amber-300 text-amber-700 rounded-md hover:bg-amber-100">
            {{ showIgnoredSection ? 'Hide' : 'Show' }} Ignored ({{ ignoredCampaigns.length }})
          </button>
        </div>
      </div>
    </div>

    <!-- Ignored Campaigns Section -->
    <div v-if="showIgnoredSection" class="bg-amber-50 rounded-lg shadow border border-amber-200 overflow-hidden">
      <div class="px-6 py-4 border-b border-amber-200">
        <h3 class="text-lg font-medium text-amber-900">Ignored Campaigns</h3>
        <p class="text-sm text-amber-700 mt-1">These campaigns are temporarily hidden from the sync log.</p>
      </div>
      <div v-if="loadingIgnored" class="p-4">
        <div class="text-sm text-amber-700">Loading ignored campaigns...</div>
      </div>
      <div v-else-if="ignoredCampaigns.length === 0" class="p-4">
        <div class="text-sm text-amber-700">No campaigns are currently ignored.</div>
      </div>
      <div v-else class="overflow-x-auto">
        <table class="min-w-full divide-y divide-amber-200">
          <thead class="bg-amber-100">
            <tr>
              <th class="px-4 py-2 text-left text-xs font-medium text-amber-900 uppercase tracking-wider">Campaign</th>
              <th class="px-4 py-2 text-left text-xs font-medium text-amber-900 uppercase tracking-wider">Ignored By</th>
              <th class="px-4 py-2 text-left text-xs font-medium text-amber-900 uppercase tracking-wider">Reason</th>
              <th class="px-4 py-2 text-left text-xs font-medium text-amber-900 uppercase tracking-wider">Expires</th>
              <th class="px-4 py-2"></th>
            </tr>
          </thead>
          <tbody class="bg-white divide-y divide-amber-200">
            <tr v-for="ignored in ignoredCampaigns" :key="ignored.id">
              <td class="px-4 py-2 text-sm">
                <div class="font-medium text-gray-900">{{ ignored.campaign.name }}</div>
                <div class="text-gray-500 text-xs">#{{ ignored.campaign.id }}</div>
              </td>
              <td class="px-4 py-2 text-sm text-gray-700">{{ ignored.ignored_by || 'System' }}</td>
              <td class="px-4 py-2 text-sm text-gray-600">{{ ignored.reason || '—' }}</td>
              <td class="px-4 py-2 text-sm">
                <div class="text-gray-700">{{ ignored.expires_at }}</div>
                <div class="text-xs text-amber-600">{{ ignored.days_remaining }} days left</div>
              </td>
              <td class="px-4 py-2 text-sm text-right">
                <button @click="unignoreCampaign(ignored.campaign.id)" class="px-3 py-1.5 border border-amber-300 rounded-md text-sm bg-white hover:bg-amber-50">
                  Unignore
                </button>
              </td>
            </tr>
          </tbody>
        </table>
      </div>
    </div>

    <!-- Main Data Table -->
    <div class="bg-white rounded-lg shadow border overflow-hidden">
      <!-- Pagination Info Bar -->
      <div class="px-6 py-3 bg-gray-50 border-b flex items-center justify-between">
        <div class="text-sm text-gray-700">
          Showing <span class="font-medium">{{ pagination.from || 0 }}</span> to <span class="font-medium">{{ pagination.to || 0 }}</span> of <span class="font-medium">{{ pagination.total || 0 }}</span> campaigns
          <span v-if="meta.cached_count" class="ml-3 text-gray-500">({{ meta.cached_count }} cached, {{ meta.fresh_count }} fresh)</span>
        </div>

        <!-- Pagination Controls -->
        <div class="flex items-center space-x-2">
          <button @click="changePage(currentPage - 1)"
                  :disabled="currentPage === 1 || loading"
                  class="px-3 py-1 border rounded-md text-sm bg-white hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed">
            Previous
          </button>

          <template v-for="page in paginationRange" :key="page">
            <button v-if="page !== '...'"
                    @click="changePage(page)"
                    :class="[
                      'px-3 py-1 border rounded-md text-sm',
                      currentPage === page ? 'bg-blue-600 text-white' : 'bg-white hover:bg-gray-50'
                    ]">
              {{ page }}
            </button>
            <span v-else class="px-2 py-1 text-gray-500">...</span>
          </template>

          <button @click="changePage(currentPage + 1)"
                  :disabled="currentPage === pagination.last_page || loading"
                  class="px-3 py-1 border rounded-md text-sm bg-white hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed">
            Next
          </button>
        </div>
      </div>

      <!-- Table -->
      <div v-if="loading" class="p-6">
        <div class="animate-pulse space-y-3">
          <div class="h-4 bg-gray-200 rounded w-full"></div>
          <div class="h-4 bg-gray-200 rounded w-full"></div>
          <div class="h-4 bg-gray-200 rounded w-full"></div>
          <div class="h-4 bg-gray-200 rounded w-full"></div>
        </div>
      </div>

      <div v-else class="overflow-x-auto">
        <table class="min-w-full divide-y divide-gray-200">
          <thead class="bg-gray-50">
            <tr>
              <th class="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider w-8"></th>
              <th class="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Campaign</th>
              <th class="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Email</th>
              <th class="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Sync Status</th>
              <th class="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Amount Sync</th>
              <th class="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Issues</th>
              <th class="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Last Checked</th>
              <th class="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">Actions</th>
            </tr>
          </thead>
          <tbody class="bg-white divide-y divide-gray-200">
            <template v-for="row in rows" :key="row.campaign.id">
              <tr :class="getRowClass(row)" class="cursor-pointer hover:bg-gray-50" @click="toggleExpandRow(row.campaign.id)">
                <!-- Expand Button -->
                <td class="px-2 py-3 text-sm w-8">
                  <button class="p-1 text-gray-400 hover:text-gray-600 transition-transform" :class="{ 'rotate-90': expandedRowId === row.campaign.id }">
                    <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 5l7 7-7 7"></path>
                    </svg>
                  </button>
                </td>
                <!-- Campaign -->
                <td class="px-4 py-3 text-sm">
                  <div class="flex items-center gap-2">
                    <span class="font-medium text-gray-900">{{ row.campaign.name }}</span>
                    <span v-if="row.failure_count > 2" class="px-1.5 py-0.5 bg-red-100 text-red-700 text-xs rounded-full" :title="`Failed ${row.failure_count} times in last 7 days`">
                      Failed {{ row.failure_count }}x
                    </span>
                  </div>
                  <div class="text-gray-500 text-xs">#{{ row.campaign.id }} • {{ row.campaign.created_at }}</div>
                </td>
                <!-- Email -->
                <td class="px-4 py-3 text-sm" @click.stop>
                  <button v-if="row.email" @click="copyEmail(row.email)" class="inline-flex items-center text-blue-600 hover:text-blue-800" type="button" title="Copy email">
                    <code class="px-1 py-0.5 bg-blue-50 rounded">{{ row.email }}</code>
                    <svg class="w-4 h-4 ml-1" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z" />
                    </svg>
                  </button>
                  <span v-else class="text-gray-400">—</span>
                </td>
                <!-- Sync Status -->
                <td class="px-4 py-3 text-sm">
                  <span :class="getSyncStatusClass(row.sync_status)">
                    <span v-if="row.sync_status === 'synced'">✅ Synced</span>
                    <span v-else-if="row.sync_status === 'not_synced'">⚪ Not Synced</span>
                    <span v-else-if="row.sync_status === 'error'">❌ Error</span>
                    <span v-else-if="row.sync_status === 'checking'">🔄 Checking</span>
                    <span v-else-if="row.sync_status === 'pending'">⏳ Pending</span>
                    <span v-else>{{ row.sync_status || 'Unknown' }}</span>
                  </span>
                </td>
                <!-- Amount Sync -->
                <td class="px-4 py-3 text-sm" @click.stop>
                  <div v-if="hasAmountMismatch(row)" class="space-y-1">
                    <div class="flex items-center gap-2">
                      <span :class="getAmountSeverityClass(row)" class="px-1.5 py-0.5 rounded text-xs font-medium">
                        {{ getAmountSeverityLabel(row) }}
                      </span>
                    </div>
                    <div class="text-xs text-gray-600">
                      <div>Local: <span class="font-medium">${{ formatAmount(row.amount_info?.local_amount) }}</span></div>
                      <div>SF: <span class="font-medium">${{ formatAmount(row.amount_info?.salesforce_amount) }}</span></div>
                      <div class="text-red-600">Diff: ${{ formatAmount(row.amount_info?.difference) }}</div>
                    </div>
                    <button
                      @click="syncCampaignAmount(row)"
                      :disabled="syncingAmountId === row.campaign.id"
                      class="mt-1 px-2 py-1 text-xs bg-purple-600 text-white rounded hover:bg-purple-700 disabled:opacity-50 flex items-center gap-1"
                    >
                      <svg v-if="syncingAmountId === row.campaign.id" class="w-3 h-3 animate-spin" fill="none" viewBox="0 0 24 24">
                        <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
                        <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                      </svg>
                      <svg v-else class="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15"></path>
                      </svg>
                      Sync Amount
                    </button>
                  </div>
                  <div v-else-if="row.amount_info && row.amount_info.is_synced" class="flex items-center gap-1.5">
                    <span class="text-green-600">
                      <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M5 13l4 4L19 7"></path>
                      </svg>
                    </span>
                    <span class="text-xs text-green-700">${{ formatAmount(row.amount_info?.local_amount) }}</span>
                  </div>
                  <div v-else-if="row.amount_info && !row.amount_info.opportunity_id" class="text-xs text-gray-400">
                    No Opportunity
                  </div>
                  <span v-else class="text-gray-400 text-xs">--</span>
                </td>
                <!-- Issues (replaces Health + Metrics) -->
                <td class="px-4 py-3 text-sm">
                  <div v-if="row.issue_summaries && row.issue_summaries.length > 0" class="space-y-1">
                    <div v-for="(issue, idx) in row.issue_summaries.slice(0, 3)" :key="idx" class="flex items-center gap-1.5">
                      <span class="flex-shrink-0">{{ getIssueIcon(issue.type) }}</span>
                      <span class="text-xs text-gray-700 truncate max-w-xs" :title="issue.summary">{{ issue.summary }}</span>
                    </div>
                    <div v-if="row.issue_summaries.length > 3" class="text-xs text-gray-500">+{{ row.issue_summaries.length - 3 }} more...</div>
                  </div>
                  <div v-else-if="row.is_permanently_healthy || row.is_healthy" class="flex items-center gap-1.5">
                    <span class="text-green-600">✅</span>
                    <span class="text-xs text-green-700">Healthy</span>
                  </div>
                  <div v-else-if="row.sync_status === 'pending' || row.sync_status === 'checking'" class="text-xs text-gray-400">
                    Checking...
                  </div>
                  <span v-else class="text-gray-400 text-xs">—</span>
                </td>
                <!-- Last Checked -->
                <td class="px-4 py-3 text-sm text-gray-600">
                  <div v-if="row.last_check_at">{{ formatDate(row.last_check_at) }}</div>
                  <div v-if="row.from_cache" class="text-xs text-blue-600">Cached</div>
                  <div v-else-if="row.last_check_at" class="text-xs text-green-600">Fresh</div>
                  <div v-else class="text-gray-400">Never</div>
                </td>
              <!-- Actions -->
              <td class="px-4 py-3 text-sm" @click.stop>
                <div class="relative inline-block text-left" :ref="`dropdown-${row.campaign.id}`">
                  <button
                    @click="toggleDropdown(row.campaign.id)"
                    type="button"
                    class="inline-flex items-center justify-center w-8 h-8 rounded-md hover:bg-gray-100 focus:outline-none focus:ring-2 focus:ring-offset-0 focus:ring-blue-500"
                    :aria-expanded="openDropdownId === row.campaign.id"
                    aria-haspopup="true">
                    <svg class="w-5 h-5 text-gray-500" viewBox="0 0 20 20" fill="currentColor">
                      <path d="M10 6a2 2 0 110-4 2 2 0 010 4zM10 12a2 2 0 110-4 2 2 0 010 4zM10 18a2 2 0 110-4 2 2 0 010 4z" />
                    </svg>
                  </button>

                  <transition
                    enter-active-class="transition ease-out duration-100"
                    enter-from-class="transform opacity-0 scale-95"
                    enter-to-class="transform opacity-100 scale-100"
                    leave-active-class="transition ease-in duration-75"
                    leave-from-class="transform opacity-100 scale-100"
                    leave-to-class="transform opacity-0 scale-95">
                    <div
                      v-if="openDropdownId === row.campaign.id"
                      class="absolute right-0 z-10 mt-2 w-56 origin-top-right rounded-md bg-white shadow-lg ring-1 ring-black ring-opacity-5 focus:outline-none"
                      role="menu"
                      aria-orientation="vertical">
                      <div class="py-1" role="none">
                        <a
                          v-if="row.links && row.links.salesforce"
                          :href="row.links.salesforce"
                          target="_blank"
                          class="flex items-center px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 hover:text-gray-900"
                          role="menuitem">
                          <svg class="mr-3 h-5 w-5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14"></path>
                          </svg>
                          View in Salesforce
                        </a>
                        <a
                          v-if="row.links && row.links.detail"
                          :href="row.links.detail"
                          class="flex items-center px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 hover:text-gray-900"
                          role="menuitem">
                          <svg class="mr-3 h-5 w-5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"></path>
                          </svg>
                          View Details
                        </a>
                        <div v-if="(row.links && (row.links.salesforce || row.links.detail))" class="border-t border-gray-100"></div>
                        <button
                          @click="refreshCampaign(row)"
                          class="flex items-center w-full px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 hover:text-gray-900 text-left"
                          role="menuitem">
                          <svg class="mr-3 h-5 w-5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15"></path>
                          </svg>
                          Retry
                        </button>
                        <button
                          @click="clearCampaignCache(row)"
                          class="flex items-center w-full px-4 py-2 text-sm text-red-600 hover:bg-red-50 hover:text-red-700 text-left"
                          role="menuitem">
                          <svg class="mr-3 h-5 w-5 text-red-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16"></path>
                          </svg>
                          Clear Cache
                        </button>
                        <div class="border-t border-gray-100"></div>
                        <button
                          @click="ignoreCampaign(row)"
                          class="flex items-center w-full px-4 py-2 text-sm text-amber-600 hover:bg-amber-50 hover:text-amber-700 text-left"
                          role="menuitem">
                          <svg class="mr-3 h-5 w-5 text-amber-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M18.364 18.364A9 9 0 005.636 5.636m12.728 12.728A9 9 0 015.636 5.636m12.728 12.728L5.636 5.636"></path>
                          </svg>
                          Ignore
                        </button>
                      </div>
                    </div>
                  </transition>
                </div>
              </td>
              </tr>

              <!-- Expandable Details Row -->
              <tr v-if="expandedRowId === row.campaign.id" class="bg-gray-50">
                <td colspan="8" class="px-4 py-4">
                  <div class="space-y-4">
                    <!-- Salesforce Data Grid -->
                    <div v-if="row.salesforce_data" class="grid grid-cols-1 md:grid-cols-3 gap-4">
                      <!-- Leads -->
                      <div v-if="row.salesforce_data.leads && row.salesforce_data.leads.length > 0" class="bg-white rounded-lg border p-3">
                        <h4 class="font-medium text-sm text-gray-900 mb-2 flex items-center">
                          <span class="mr-2">📋</span> Leads ({{ row.salesforce_data.leads.length }})
                        </h4>
                        <div class="space-y-1.5">
                          <div v-for="lead in row.salesforce_data.leads" :key="lead.id" class="flex items-center justify-between text-xs">
                            <div>
                              <a :href="lead.sf_url" target="_blank" class="text-blue-600 hover:underline font-medium">{{ lead.name || 'Unknown' }}</a>
                              <span v-if="lead.email" class="text-gray-500 ml-1">({{ lead.email }})</span>
                            </div>
                            <span v-if="!lead.is_converted" class="px-1.5 py-0.5 bg-amber-100 text-amber-700 rounded text-xs">Unconverted</span>
                            <span v-else class="px-1.5 py-0.5 bg-green-100 text-green-700 rounded text-xs">Converted</span>
                          </div>
                        </div>
                      </div>

                      <!-- Accounts -->
                      <div v-if="row.salesforce_data.accounts && row.salesforce_data.accounts.length > 0" class="bg-white rounded-lg border p-3">
                        <h4 class="font-medium text-sm text-gray-900 mb-2 flex items-center">
                          <span class="mr-2">🏢</span> Accounts ({{ row.salesforce_data.accounts.length }})
                          <span v-if="row.salesforce_data.accounts.length > 1" class="ml-2 px-1.5 py-0.5 bg-amber-100 text-amber-700 rounded text-xs">Needs Merge</span>
                        </h4>
                        <div class="space-y-1.5">
                          <div v-for="acct in row.salesforce_data.accounts" :key="acct.id" class="text-xs">
                            <a :href="acct.sf_url" target="_blank" class="text-blue-600 hover:underline font-medium">{{ acct.name }}</a>
                          </div>
                        </div>
                      </div>

                      <!-- Opportunities -->
                      <div v-if="row.salesforce_data.opportunities && row.salesforce_data.opportunities.length > 0" class="bg-white rounded-lg border p-3">
                        <h4 class="font-medium text-sm text-gray-900 mb-2 flex items-center">
                          <span class="mr-2">💰</span> Opportunities ({{ row.salesforce_data.opportunities.length }})
                        </h4>
                        <div class="space-y-1.5">
                          <div v-for="opp in row.salesforce_data.opportunities" :key="opp.id" class="flex items-center justify-between text-xs">
                            <a :href="opp.sf_url" target="_blank" class="text-blue-600 hover:underline font-medium truncate max-w-[200px]" :title="opp.name">{{ opp.name }}</a>
                            <span class="text-gray-500">{{ opp.stage }}</span>
                          </div>
                        </div>
                      </div>

                      <!-- Contacts -->
                      <div v-if="row.salesforce_data.contacts && row.salesforce_data.contacts.length > 0" class="bg-white rounded-lg border p-3">
                        <h4 class="font-medium text-sm text-gray-900 mb-2 flex items-center">
                          <span class="mr-2">👤</span> Contacts ({{ row.salesforce_data.contacts.length }})
                        </h4>
                        <div class="space-y-1.5">
                          <div v-for="contact in row.salesforce_data.contacts" :key="contact.id" class="text-xs">
                            <a :href="contact.sf_url" target="_blank" class="text-blue-600 hover:underline font-medium">{{ contact.name || 'Unknown' }}</a>
                            <span v-if="contact.email" class="text-gray-500 ml-1">({{ contact.email }})</span>
                          </div>
                        </div>
                      </div>
                    </div>

                    <!-- No Salesforce Data Message -->
                    <div v-else-if="row.from_cache" class="text-sm text-gray-500 bg-white rounded-lg border p-4">
                      <p>Detailed Salesforce records not available from cache.</p>
                      <button @click="refreshCampaign(row)" class="mt-2 text-blue-600 hover:underline text-sm">
                        Click to refresh and load full details
                      </button>
                    </div>

                    <!-- Action Buttons -->
                    <div class="flex flex-wrap gap-2 pt-2 border-t">
                      <button @click="refreshCampaign(row)" class="px-3 py-1.5 bg-blue-600 text-white text-xs rounded hover:bg-blue-700 flex items-center gap-1">
                        <svg class="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15"></path>
                        </svg>
                        Retry Sync
                      </button>

                      <a v-if="getUnconvertedLeadUrl(row)" :href="getUnconvertedLeadUrl(row)" target="_blank"
                         class="px-3 py-1.5 bg-amber-600 text-white text-xs rounded hover:bg-amber-700 flex items-center gap-1">
                        <svg class="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"></path>
                        </svg>
                        Convert Lead in Salesforce
                      </a>

                      <button v-if="hasMissingOpportunity(row)"
                              @click="createMissingOpportunity(row)"
                              :disabled="creatingOpportunity === row.campaign.id"
                              class="px-3 py-1.5 bg-green-600 text-white text-xs rounded hover:bg-green-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-1">
                        <svg v-if="creatingOpportunity === row.campaign.id" class="w-3.5 h-3.5 animate-spin" fill="none" viewBox="0 0 24 24">
                          <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
                          <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                        </svg>
                        <svg v-else class="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 6v6m0 0v6m0-6h6m-6 0H6"></path>
                        </svg>
                        {{ creatingOpportunity === row.campaign.id ? 'Creating...' : 'Create Missing Opportunity' }}
                      </button>

                      <button v-if="hasDuplicateOpportunities(row)"
                              @click="deleteDuplicateOpportunity(row)"
                              :disabled="deletingDuplicate === row.campaign.id"
                              class="px-3 py-1.5 bg-red-600 text-white text-xs rounded hover:bg-red-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-1">
                        <svg v-if="deletingDuplicate === row.campaign.id" class="w-3.5 h-3.5 animate-spin" fill="none" viewBox="0 0 24 24">
                          <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
                          <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                        </svg>
                        <svg v-else class="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16"></path>
                        </svg>
                        {{ deletingDuplicate === row.campaign.id ? 'Deleting...' : 'Delete Duplicate Opportunity' }}
                      </button>

                      <a v-if="getMergeAccountsUrl(row)" :href="getMergeAccountsUrl(row)" target="_blank"
                         class="px-3 py-1.5 bg-purple-600 text-white text-xs rounded hover:bg-purple-700 flex items-center gap-1">
                        <svg class="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M8 7h12m0 0l-4-4m4 4l-4 4m0 6H4m0 0l4 4m-4-4l4-4"></path>
                        </svg>
                        Merge Accounts in Salesforce
                      </a>

                      <a v-if="row.links && row.links.salesforce" :href="row.links.salesforce" target="_blank"
                         class="px-3 py-1.5 bg-gray-600 text-white text-xs rounded hover:bg-gray-700 flex items-center gap-1">
                        <svg class="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14"></path>
                        </svg>
                        Open in Salesforce
                      </a>

                      <a v-if="row.links && row.links.detail" :href="row.links.detail"
                         class="px-3 py-1.5 border border-gray-300 text-gray-700 text-xs rounded hover:bg-gray-50 flex items-center gap-1">
                        <svg class="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"></path>
                        </svg>
                        View Full Report
                      </a>
                    </div>
                  </div>
                </td>
              </tr>
            </template>

            <tr v-if="rows.length === 0">
              <td colspan="8" class="px-6 py-8 text-center text-sm text-gray-500">No campaigns found.</td>
            </tr>
          </tbody>
        </table>
      </div>
    </div>
  </div>
</template>

<script>
export default {
  name: 'SalesforceCampaignHealthPaginated',
  data() {
    return {
      loading: false,
      rows: [],
      pagination: {
        total: 0,
        per_page: 50,
        current_page: 1,
        last_page: 1,
        from: 0,
        to: 0,
      },
      meta: {
        cached_count: 0,
        fresh_count: 0,
      },
      filters: {
        days: 30,
        dpName: 'Dig Pink Rally',
        statusFilter: 'all',
        healthFilter: 'all',
        amountFilter: 'all',
      },
      perPage: 50,
      currentPage: 1,
      ignoredCampaigns: [],
      showIgnoredSection: false,
      loadingIgnored: false,
      checkingCampaigns: {},
      uncachedCampaigns: [],
      openDropdownId: null,
      expandedRowId: null,
      creatingOpportunity: null,
      deletingDuplicate: null,
      // Bulk sync state
      bulkSyncRunning: false,
      // Individual amount sync state
      syncingAmountId: null,
    }
  },
  computed: {
    paginationRange() {
      const range = []
      const lastPage = this.pagination.last_page || 1
      const current = this.currentPage
      const delta = 2

      // Always show first page
      if (current > delta + 1) {
        range.push(1)
        if (current > delta + 2) range.push('...')
      }

      // Show pages around current
      for (let i = Math.max(1, current - delta); i <= Math.min(lastPage, current + delta); i++) {
        range.push(i)
      }

      // Always show last page
      if (current < lastPage - delta) {
        if (current < lastPage - delta - 1) range.push('...')
        range.push(lastPage)
      }

      return range
    },
    /**
     * Whether a sync is currently in progress (from Vuex store)
     */
    syncInProgress() {
      return this.$store?.state?.salesforceSync?.isRunning || false
    },
  },
  methods: {
    toggleDropdown(campaignId) {
      if (this.openDropdownId === campaignId) {
        this.openDropdownId = null
      } else {
        this.openDropdownId = campaignId
      }
    },
    toggleExpandRow(campaignId) {
      // Close dropdown when expanding
      this.openDropdownId = null
      if (this.expandedRowId === campaignId) {
        this.expandedRowId = null
      } else {
        this.expandedRowId = campaignId
      }
    },
    getIssueIcon(type) {
      const icons = {
        'unconverted_leads': '📋',
        'missing_opportunity': '📊',
        'duplicate_opportunities': '📊',
        'duplicate_accounts': '🏢',
      }
      return icons[type] || '⚠️'
    },
    getUnconvertedLeadUrl(row) {
      if (!row.salesforce_data || !row.salesforce_data.leads) return null
      const unconvertedLead = row.salesforce_data.leads.find(l => !l.is_converted)
      return unconvertedLead ? unconvertedLead.sf_url : null
    },
    getMergeAccountsUrl(row) {
      if (!row.salesforce_data || !row.salesforce_data.accounts) return null
      if (row.salesforce_data.accounts.length <= 1) return null
      // Return the merge URL for the first account
      return row.salesforce_data.accounts[0]?.merge_url || null
    },
    hasMissingOpportunity(row) {
      // Check if this row has a 'missing_opportunity' issue
      if (!row.issue_summaries || !Array.isArray(row.issue_summaries)) return false
      return row.issue_summaries.some(s => s.type === 'missing_opportunity')
    },
    async createMissingOpportunity(row) {
      if (!row.campaign || !row.campaign.id) return

      const campaignId = row.campaign.id
      this.creatingOpportunity = campaignId

      try {
        const url = `/admin/salesforce/health-report/create-opportunity/${campaignId}`
        await this.requestJson(url, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'X-CSRF-TOKEN': document.querySelector('meta[name="csrf-token"]')?.content || '',
          },
        })

        this.$notify && this.$notify({
          type: 'success',
          text: 'Job dispatched to create missing opportunity. Refreshing status...'
        })

        // Wait a bit then refresh the row
        setTimeout(() => {
          this.refreshCampaign(row)
        }, 2000)

      } catch (error) {
        this.handleError(error, 'Failed to create missing opportunity')
      } finally {
        this.creatingOpportunity = null
      }
    },
    hasDuplicateOpportunities(row) {
      // Check if this row has a 'duplicate_opportunities' issue
      if (!row.issue_summaries || !Array.isArray(row.issue_summaries)) return false
      return row.issue_summaries.some(s => s.type === 'duplicate_opportunities')
    },
    async deleteDuplicateOpportunity(row) {
      if (!row.campaign || !row.campaign.id) return

      const campaignId = row.campaign.id
      this.deletingDuplicate = campaignId

      try {
        const url = `/admin/salesforce/health-report/delete-duplicate-opportunity/${campaignId}`
        const result = await this.requestJson(url, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'X-CSRF-TOKEN': document.querySelector('meta[name="csrf-token"]')?.content || '',
          },
        })

        this.$notify && this.$notify({
          type: 'success',
          text: result.message || 'Job dispatched to delete duplicate opportunity. Refreshing status...'
        })

        // Wait a bit then refresh the row
        setTimeout(() => {
          this.refreshCampaign(row)
        }, 2000)

      } catch (error) {
        this.handleError(error, 'Failed to delete duplicate opportunity')
      } finally {
        this.deletingDuplicate = null
      }
    },
    handleClickOutside(event) {
      if (this.openDropdownId) {
        const dropdownRef = this.$refs[`dropdown-${this.openDropdownId}`]
        if (dropdownRef && dropdownRef[0] && !dropdownRef[0].contains(event.target)) {
          this.openDropdownId = null
        }
      }
    },
    async requestJson(url, options = {}) {
      const response = await fetch(url, options)
      const contentType = (response.headers.get('content-type') || '').toLowerCase()
      let payload = null

      if (contentType.includes('application/json')) {
        payload = await response.json()
      } else {
        const text = await response.text()
        const message = text ? text.slice(0, 200) : `${response.status} ${response.statusText}`
        const error = new Error(message)
        error.status = response.status
        throw error
      }

      if (!response.ok || (payload && payload.success === false)) {
        const message = (payload && payload.message) ? payload.message : `${response.status} ${response.statusText}`
        const error = new Error(message)
        error.status = response.status
        error.payload = payload
        throw error
      }

      return payload
    },
    handleError(error, fallbackMessage = 'Request failed') {
      console.error(fallbackMessage, error)
      const message = (error && error.message) ? error.message : fallbackMessage
      this.$notify && this.$notify({ type: 'error', text: message })
    },
    getMaxChecksPerRequest() {
      const raw = this.meta && (this.meta.max_checks_per_request ?? this.meta.maxChecksPerRequest)
      const parsed = Number(raw)
      return Number.isFinite(parsed) && parsed > 0 ? parsed : 5
    },
    async loadPage(forceRefresh = false) {
      this.loading = true
      try {
        const params = new URLSearchParams()
        params.set('page', String(this.currentPage))
        params.set('per_page', String(this.perPage))
        params.set('days', String(this.filters.days))
        params.set('dp_name', this.filters.dpName)
        params.set('status_filter', this.filters.statusFilter)
        params.set('health_filter', this.filters.healthFilter)
        params.set('amount_filter', this.filters.amountFilter)
        if (forceRefresh) params.set('force_refresh', '1')

        const json = await this.requestJson('/admin/salesforce/health-report/paginated?' + params.toString())

        this.rows = json.data || []
        this.pagination = json.pagination || {}
        this.meta = json.meta || {}

        // Find campaigns that need checking (dedupe ids)
        const pendingIds = this.rows.filter(r =>
          r.sync_status === 'pending' || r.sync_status === 'checking' || !r.from_cache
        ).map(r => r.campaign.id)
        this.uncachedCampaigns = Array.from(new Set(pendingIds))
      } catch (e) {
        this.handleError(e, 'Failed to load campaigns')
      } finally {
        this.loading = false
      }
    },
    async checkUncachedCampaigns() {
      if (this.uncachedCampaigns.length === 0) return

      const batchSize = this.getMaxChecksPerRequest()
      const campaignIds = this.uncachedCampaigns.slice(0, batchSize)
      if (campaignIds.length === 0) return

      const tokenEl = document.querySelector('meta[name="csrf-token"]')
      const token = tokenEl ? tokenEl.getAttribute('content') : ''

      try {
        const json = await this.requestJson('/admin/salesforce/health-report/check-campaigns', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'X-CSRF-TOKEN': token,
            'Accept': 'application/json'
          },
          body: JSON.stringify({
            campaign_ids: campaignIds,
            dp_name: this.filters.dpName
          })
        })

        const processed = Array.isArray(json.results) ? json.results.length : 0
        const remaining = Array.isArray(json.remaining_ids) ? json.remaining_ids.length : 0
        const parts = [`Checked ${processed} campaign${processed === 1 ? '' : 's'}`]
        if (remaining > 0) {
          parts.push(`${remaining} still pending`)
        }

        this.$notify && this.$notify({ type: 'success', text: parts.join('. ') })

        if (Array.isArray(json.remaining_ids) && json.remaining_ids.length > 0) {
          this.uncachedCampaigns = json.remaining_ids
        }

        await this.loadPage()
      } catch (e) {
        this.handleError(e, 'Failed to check campaigns')
      }
    },
    async refreshCampaign(row) {
      this.openDropdownId = null
      // Clear cache and reload
      const cleared = await this.clearCampaignCache(row, false)

      if (!cleared) {
        return
      }

      // Check this specific campaign
      const tokenEl = document.querySelector('meta[name="csrf-token"]')
      const token = tokenEl ? tokenEl.getAttribute('content') : ''

      try {
        await this.requestJson('/admin/salesforce/health-report/check-campaigns', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'X-CSRF-TOKEN': token,
            'Accept': 'application/json'
          },
          body: JSON.stringify({
            campaign_ids: [row.campaign.id],
            dp_name: this.filters.dpName
          })
        })

        this.$notify && this.$notify({ type: 'success', text: `Retried campaign #${row.campaign.id}` })
        // Reload the page to show updated data
        await this.loadPage()
      } catch (e) {
        this.handleError(e, 'Failed to retry campaign')
      }
    },
    async clearCampaignCache(row, notify = true) {
      this.openDropdownId = null
      const tokenEl = document.querySelector('meta[name="csrf-token"]')
      const token = tokenEl ? tokenEl.getAttribute('content') : ''

      try {
        await this.requestJson(`/admin/salesforce/health-report/cache/${row.campaign.id}`, {
          method: 'DELETE',
          headers: {
            'X-CSRF-TOKEN': token,
            'Accept': 'application/json'
          }
        })

        if (notify) {
          this.$notify && this.$notify({ type: 'success', text: `Cache cleared for campaign #${row.campaign.id}` })
          // Update row to show it needs checking
          const idx = this.rows.findIndex(r => r.campaign.id === row.campaign.id)
          if (idx !== -1) {
            this.rows[idx].sync_status = 'pending'
            this.rows[idx].from_cache = false
            this.rows[idx].last_check_at = null
          }
        }

        return true
      } catch (e) {
        if (notify) {
          this.handleError(e, 'Failed to clear cache')
        } else {
          console.error('Failed to clear cache', e)
        }
        return false
      }
    },
    async ignoreCampaign(row) {
      this.openDropdownId = null
      const reason = prompt('Optional: Enter a reason for ignoring this campaign (or leave blank):')
      if (reason === null) return // User cancelled

      const tokenEl = document.querySelector('meta[name="csrf-token"]')
      const token = tokenEl ? tokenEl.getAttribute('content') : ''

      try {
        await this.requestJson(`/admin/salesforce/health-report/ignore/${row.campaign.id}`, {
          method: 'POST',
          headers: {
            'X-CSRF-TOKEN': token,
            'Accept': 'application/json',
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({ reason })
        })

        this.$notify && this.$notify({ type: 'success', text: `Campaign #${row.campaign.id} ignored for 30 days` })
        // Remove from current rows
        const idx = this.rows.findIndex(r => r.campaign.id === row.campaign.id)
        if (idx !== -1) {
          this.rows.splice(idx, 1)
        }
        // Refresh ignored list
        this.loadIgnoredCampaigns()
      } catch (e) {
        this.handleError(e, 'Ignore request failed')
      }
    },
    async unignoreCampaign(campaignId) {
      if (!confirm('Are you sure you want to remove the ignore status for this campaign?')) return

      const tokenEl = document.querySelector('meta[name="csrf-token"]')
      const token = tokenEl ? tokenEl.getAttribute('content') : ''

      try {
        await this.requestJson(`/admin/salesforce/health-report/ignore/${campaignId}`, {
          method: 'DELETE',
          headers: {
            'X-CSRF-TOKEN': token,
            'Accept': 'application/json'
          }
        })

        this.$notify && this.$notify({ type: 'success', text: `Campaign #${campaignId} unignored` })
        this.loadIgnoredCampaigns()
        this.loadPage() // Reload main list
      } catch (e) {
        this.handleError(e, 'Unignore request failed')
      }
    },
    async loadIgnoredCampaigns() {
      this.loadingIgnored = true
      try {
        const json = await this.requestJson('/admin/salesforce/health-report/ignored')
        this.ignoredCampaigns = json.data || []
      } catch (e) {
        this.handleError(e, 'Failed to load ignored campaigns')
      } finally {
        this.loadingIgnored = false
      }
    },
    toggleIgnoredSection() {
      this.showIgnoredSection = !this.showIgnoredSection
      if (this.showIgnoredSection && this.ignoredCampaigns.length === 0) {
        this.loadIgnoredCampaigns()
      }
    },
    changePage(page) {
      if (page < 1 || page > this.pagination.last_page || page === this.currentPage) return
      this.currentPage = page
      this.loadPage()
    },
    applyFilters() {
      this.currentPage = 1
      this.loadPage()
    },
    getSyncStatusClass(status) {
      const classes = {
        'synced': 'px-2 py-1 rounded-full text-xs font-medium bg-green-100 text-green-700',
        'not_synced': 'px-2 py-1 rounded-full text-xs font-medium bg-gray-100 text-gray-700',
        'error': 'px-2 py-1 rounded-full text-xs font-medium bg-red-100 text-red-700',
        'checking': 'px-2 py-1 rounded-full text-xs font-medium bg-yellow-100 text-yellow-700',
        'pending': 'px-2 py-1 rounded-full text-xs font-medium bg-blue-100 text-blue-700',
      }
      return classes[status] || 'px-2 py-1 rounded-full text-xs font-medium bg-gray-100 text-gray-700'
    },
    getMetricClass(isGood) {
      return isGood
        ? 'px-2 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-700'
        : 'px-2 py-0.5 rounded-full text-xs font-medium bg-red-100 text-red-700'
    },
    getRowClass(row) {
      if (row.sync_status === 'error') return 'bg-red-50'
      if (row.is_healthy === false) return 'bg-yellow-50'
      if (!row.from_cache && row.sync_status === 'pending') return 'bg-blue-50'
      return ''
    },
    formatDate(dateStr) {
      if (!dateStr) return ''
      const date = new Date(dateStr)
      const now = new Date()
      const diffMs = now - date
      const diffMins = Math.floor(diffMs / 60000)
      const diffHours = Math.floor(diffMs / 3600000)
      const diffDays = Math.floor(diffMs / 86400000)

      if (diffMins < 1) return 'Just now'
      if (diffMins < 60) return `${diffMins} min${diffMins > 1 ? 's' : ''} ago`
      if (diffHours < 24) return `${diffHours} hour${diffHours > 1 ? 's' : ''} ago`
      if (diffDays < 7) return `${diffDays} day${diffDays > 1 ? 's' : ''} ago`

      return date.toLocaleDateString()
    },
    async copyEmail(email) {
      try {
        await navigator.clipboard.writeText(email)
        this.$notify && this.$notify({ type: 'success', text: `Email copied: ${email}` })
      } catch (e) {
        // Fallback
        const ta = document.createElement('textarea')
        ta.value = email
        ta.style.position = 'fixed'
        ta.style.top = '-1000px'
        document.body.appendChild(ta)
        ta.focus()
        ta.select()
        document.execCommand('copy')
        document.body.removeChild(ta)
        this.$notify && this.$notify({ type: 'success', text: `Email copied: ${email}` })
      }
    },
    savePrefs() {
      try {
        localStorage.setItem('sfHealthPaginatedPrefs', JSON.stringify({
          filters: this.filters,
          perPage: this.perPage
        }))
      } catch (e) { /* ignore */ }
    },
    loadPrefs() {
      try {
        const raw = localStorage.getItem('sfHealthPaginatedPrefs')
        if (!raw) return
        const obj = JSON.parse(raw)
        if (obj && typeof obj === 'object') {
          if (obj.filters) this.filters = { ...this.filters, ...obj.filters }
          if (obj.perPage) this.perPage = obj.perPage
        }
      } catch (e) { /* ignore */ }
    },

    // =====================================================
    // Bulk Sync Methods
    // =====================================================

    /**
     * Start a bulk sync operation for all campaigns with amount mismatches
     */
    async startBulkSync() {
      if (this.bulkSyncRunning || this.syncInProgress) {
        return
      }

      if (!confirm('Start bulk sync of all campaign donation amounts to Salesforce? This may take several minutes.')) {
        return
      }

      this.bulkSyncRunning = true

      try {
        // Use the Vuex store to start the bulk sync
        await this.$store.dispatch('salesforceSync/startBulkSync', {
          syncType: 'all_campaigns'
        })

        this.$notify && this.$notify({
          type: 'success',
          text: 'Bulk sync started. Progress will be shown in the banner above.'
        })
      } catch (error) {
        this.handleError(error, 'Failed to start bulk sync')
      } finally {
        this.bulkSyncRunning = false
      }
    },

    // =====================================================
    // Amount Sync Methods
    // =====================================================

    /**
     * Check if a row has an amount mismatch
     */
    hasAmountMismatch(row) {
      if (!row.amount_info) return false
      return row.amount_info.has_mismatch === true
    },

    /**
     * Get the severity class for amount mismatch display
     */
    getAmountSeverityClass(row) {
      if (!row.amount_info || !row.amount_info.difference) {
        return 'bg-gray-100 text-gray-700'
      }

      const diff = Math.abs(row.amount_info.difference)

      if (diff >= 1000) {
        return 'bg-red-100 text-red-800'
      } else if (diff >= 100) {
        return 'bg-amber-100 text-amber-800'
      } else {
        return 'bg-yellow-100 text-yellow-800'
      }
    },

    /**
     * Get the severity label for amount mismatch
     */
    getAmountSeverityLabel(row) {
      if (!row.amount_info || !row.amount_info.difference) {
        return 'Unknown'
      }

      const diff = Math.abs(row.amount_info.difference)

      if (diff >= 1000) {
        return 'Critical'
      } else if (diff >= 100) {
        return 'Warning'
      } else {
        return 'Minor'
      }
    },

    /**
     * Format an amount for display
     */
    formatAmount(amount) {
      if (amount === null || amount === undefined) return '0.00'
      return parseFloat(amount).toLocaleString('en-US', {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2
      })
    },

    /**
     * Sync the amount for a single campaign
     */
    async syncCampaignAmount(row) {
      if (!row.campaign || !row.campaign.id) return

      const campaignId = row.campaign.id
      this.syncingAmountId = campaignId

      try {
        const url = `/admin/salesforce/amount-sync/sync/${campaignId}`
        await this.requestJson(url, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'X-CSRF-TOKEN': document.querySelector('meta[name="csrf-token"]')?.content || '',
          },
        })

        this.$notify && this.$notify({
          type: 'success',
          text: `Amount sync queued for campaign #${campaignId}. Refreshing...`
        })

        // Wait a bit then refresh the row
        setTimeout(() => {
          this.refreshCampaign(row)
        }, 2000)

      } catch (error) {
        this.handleError(error, 'Failed to sync campaign amount')
      } finally {
        this.syncingAmountId = null
      }
    }
  },
  watch: {
    filters: {
      handler() { this.savePrefs() },
      deep: true
    },
    perPage() {
      this.savePrefs()
      this.currentPage = 1
      this.loadPage()
    }
  },
  mounted() {
    this.loadPrefs()
    this.loadPage()
    this.loadIgnoredCampaigns()
    // Add click outside listener
    document.addEventListener('click', this.handleClickOutside)
  },
  beforeUnmount() {
    // Clean up click outside listener
    document.removeEventListener('click', this.handleClickOutside)
  }
}
</script>